/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo } from 'react';
import { useServiceDeskContext } from '@jira/servicedesk__servicedesk-context/src/useServiceDeskContext.tsx';
import { projectContextResourceWithCache } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type { Locale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import type { PracticeQueueRouteParams } from '@jira/navigation-apps__sidebar-common/src/controllers/common/project-sidebar-navigation-resource/types.tsx';
import type { UseResourceCustomContext } from '@jira/platform__routing-router-resource-with-custom-router-context/src/controllers/types.tsx';
import { useResourceWithCustomRouterContext } from '@jira/platform__routing-router-resource-with-custom-router-context/src/controllers/use-resource-with-custom-router-context/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';

const getProjectContextResource = () => projectContextResourceWithCache;

export const usePageContext = (
	customContext?: UseResourceCustomContext<PracticeQueueRouteParams>,
): readonly [
	{
		data: {
			serviceDeskContext: {
				portalId: number;
				portalUrl: string;
				canSeeNewIssueView: boolean;
				userRole: string;
				serviceDeskId: number;
			};
			projectContext: {
				projectId: number;
				projectKey: string;
				projectName: string;
				projectType: ProjectType;
				// Refactor this in https://jdog.jira-dev.com/browse/JSK-443 this is a temporary fix to
				// resolve a regression https://jira.atlassian.com/browse/JSDCLOUD-8751
				isAdmin: boolean;
				isProjectSimplified: boolean;
			};
			tenantContext: {
				locale: Locale;
				userFullname: string | null;
				cloudId: string;
				fabricPfDirUrl: string | null;
				atlassianAccountId: string | null;
				isJiraAdmin: boolean;
				isSiteAdmin: boolean;
				activationId: string;
			};
		} | null;
		loading: boolean;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		error: Record<string, any> | Error | null;
	},
] => {
	const tenantContext = useTenantContext();

	const {
		data: projectContext,
		loading: projectContextLoading,
		error: projectContextError,
	} = useResourceWithCustomRouterContext(getProjectContextResource(), customContext);

	const [{ data: serviceDeskContext, loading: serviceDeskLoading, error: serviceDeskError }] =
		useServiceDeskContext(projectContext?.projectKey || undefined, customContext);

	const pageContextData = useMemo(
		() => ({
			data:
				projectContext && serviceDeskContext
					? {
							serviceDeskContext: {
								portalId: serviceDeskContext.portalId,
								portalUrl: serviceDeskContext.portalUrl,
								canSeeNewIssueView: serviceDeskContext.canSeeNewIssueView,
								userRole: serviceDeskContext.userRole,
								serviceDeskId: serviceDeskContext.serviceDeskId,
							},

							projectContext: {
								projectId: projectContext.projectId,
								projectKey: projectContext.projectKey,
								projectName: projectContext.projectName,
								projectType: projectContext.projectType,
								// Refactor this in https://jdog.jira-dev.com/browse/JSK-443 this is a temporary fix to
								// resolve a regression https://jira.atlassian.com/browse/JSDCLOUD-8751
								isAdmin: projectContext.isProjectAdmin || tenantContext.isAdmin,
								isProjectSimplified: projectContext.simplified,
							},

							tenantContext: {
								locale: tenantContext.locale,
								userFullname: tenantContext.userFullname,
								cloudId: tenantContext.cloudId,
								fabricPfDirUrl: tenantContext.fabricPfDirUrl,
								atlassianAccountId: tenantContext.atlassianAccountId,
								isJiraAdmin: tenantContext.isAdmin,
								isSiteAdmin: tenantContext.isSiteAdmin,
								activationId: tenantContext.activationId,
							},
						}
					: null,
			loading: projectContextLoading || serviceDeskLoading,
			error: serviceDeskError || projectContextError || null,
		}),
		[
			tenantContext,
			projectContext,
			serviceDeskContext,
			projectContextLoading,
			serviceDeskLoading,
			serviceDeskError,
			projectContextError,
		],
	);

	return [pageContextData] as const;
};
